import { BRAND_GREEN } from "@/lib/design-tokens";

export type Platform = "bac" | "driveshare";

/** Fixed membership product identity, independent of the surrounding site brand. */
export const PRO_OWNER_PRODUCT_NAME = "DriveShare";

/** Approved shared image service; source-led landings can retain either brand's assets. */
export const LANDING_ASSET_HOST = "driveshare-assets.com";

export { PLAY_STORE_URL } from "@workspace/app-store-destinations";

export interface PlatformConfig {
  platform: Platform;
  name: string;
  /** Complete public-facing wordmark name for accessible labels and receipts. */
  fullName: string;
  shortName: string;
  domain: string;
  tagline: string;
  description: string;
  primaryColor: string;
  accentColor: string;
  logoSrc: string;
  logoSrcDark: string;
  /** Public-root favicon path (512×512 PNG, `purpose: any`). */
  faviconSrc: string;
  /** Public-root apple-touch-icon path (512×512 PNG, maskable). */
  appleTouchIconSrc: string;
  /** Public-root default Open Graph share card (1200×630 JPG). */
  ogImageSrc: string;
  /** Public-root PWA manifest path. */
  manifestSrc: string;
  emailFromName: string;
  emailFromAddress: string;
  supportEmail: string;
  blogUrl: string;
  eventsUrl: string;
  socialLinks: {
    instagram: string;
    facebook: string;
    tiktok: string;
    x: string;
    youtube: string;
  };
  features: {
    selfDrive: boolean;
    chauffeur: boolean;
    events: boolean;
    multiRegion: boolean;
  };
}

const OFFICIAL_SOCIAL_LINKS: PlatformConfig["socialLinks"] = {
  instagram: "https://www.instagram.com/driveshare_/",
  facebook: "https://www.facebook.com/driveshareofficial",
  tiktok: "https://www.tiktok.com/@driveshare_",
  x: "https://x.com/DRIVESHARE__",
  youtube: "https://www.youtube.com/@DRIVESHAREofficial",
};

/** Established external events destination shared by both brand modes. */
const FIXED_DRIVESHARE_EVENTS_URL = "https://events.driveshare.com";

const configs: Record<Platform, PlatformConfig> = {
  bac: {
    platform: "bac",
    name: "BookAclassic",
    fullName: "BookAclassic by DRIVESHARE",
    shortName: "BAC",
    domain: "www.bookaclassic.com",
    tagline: "Rent Iconic Classic Cars",
    description:
      "The world's premier marketplace for renting classic and collector cars.",
    primaryColor: BRAND_GREEN,
    accentColor: BRAND_GREEN,
    logoSrc: "/logos/bac-logo.svg",
    logoSrcDark: "/logos/bac-logo-dark.svg",
    faviconSrc: "/favicon.png",
    appleTouchIconSrc: "/apple-touch-icon.png",
    ogImageSrc: "/opengraph.jpg",
    manifestSrc: "/manifest.webmanifest",
    emailFromName: "BookAclassic",
    emailFromAddress: "hello@bookaclassic.com",
    supportEmail: "support@bookaclassic.com",
    blogUrl: "https://blog.bookaclassic.com",
    eventsUrl: FIXED_DRIVESHARE_EVENTS_URL,
    socialLinks: OFFICIAL_SOCIAL_LINKS,
    features: {
      selfDrive: true,
      chauffeur: true,
      events: true,
      multiRegion: true,
    },
  },
  driveshare: {
    platform: "driveshare",
    name: "DRIVESHARE",
    fullName: "DRIVESHARE",
    shortName: "DS",
    domain: "driveshare.com",
    tagline: "Share your passion. Drive theirs.",
    description:
      "America's largest peer-to-peer classic car rental marketplace.",
    // The runtime cutover changes identity, not the established product
    // palette. Both brands intentionally share the canonical dark green.
    primaryColor: BRAND_GREEN,
    accentColor: BRAND_GREEN,
    logoSrc: "/logos/ds-logo.svg",
    logoSrcDark: "/logos/ds-logo-dark.svg",
    faviconSrc: "/ds-favicon.png",
    appleTouchIconSrc: "/ds-apple-touch-icon.png",
    ogImageSrc: "/ds-opengraph.jpg",
    manifestSrc: "/manifest.driveshare.webmanifest",
    emailFromName: "DRIVESHARE",
    emailFromAddress: "hello@driveshare.com",
    supportEmail: "support@driveshare.com",
    blogUrl: "https://blog.driveshare.com",
    eventsUrl: FIXED_DRIVESHARE_EVENTS_URL,
    socialLinks: OFFICIAL_SOCIAL_LINKS,
    features: {
      selfDrive: true,
      chauffeur: true,
      events: true,
      multiRegion: true,
    },
  },
};

// ---------------------------------------------------------------------------
// Task #8034 — runtime brand cutover.
//
// Platform resolution order: runtime override (set from the
// `brand_cutover_driveshare` feature flag by PlatformProvider) > build-time
// env (`VITE_PLATFORM`) > "bac". The override lives in module state (not
// React state) so non-React consumers — seo.ts, pdf helpers, i18n — all
// agree with what the provider rendered. Subscribers (the provider itself,
// plus any future non-React listener) are notified on change so React can
// re-render with the new config.
// ---------------------------------------------------------------------------

function envPlatform(): Platform {
  const raw = (
    import.meta.env.NEXT_PUBLIC_PLATFORM ||
    import.meta.env.VITE_PLATFORM ||
    "bac"
  ) as string;
  return raw === "driveshare" ? "driveshare" : "bac";
}

let platformOverride: Platform | null = null;
const listeners = new Set<() => void>();

/** The effective platform: flag override > env > "bac". */
let requestPlatformReader: (() => Platform | undefined) | undefined;
/** Registered once by the Node renderer; the reader uses AsyncLocalStorage. */
export function registerRequestPlatformReader(reader: () => Platform | undefined): void {
  requestPlatformReader = reader;
}
export function getActivePlatform(): Platform {
  const requestPlatform = requestPlatformReader?.();
  if (requestPlatform) return requestPlatform;
  return platformOverride ?? envPlatform();
}

/**
 * Set (or clear, with `null`) the runtime platform override. Called by
 * PlatformProvider when the `brand_cutover_driveshare` flag resolves.
 * No-op (no listener storm) when the value is unchanged.
 */
export function setPlatformOverride(platform: Platform | null): void {
  if (platformOverride === platform) return;
  platformOverride = platform;
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      /* one bad listener must not break the rest */
    }
  }
}

/** Subscribe to platform changes; returns an unsubscribe fn. */
export function onPlatformChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPlatformConfig(platform?: Platform): PlatformConfig {
  return configs[platform ?? getActivePlatform()] || configs.bac;
}
